import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR, validateCustomerPhone } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Save, Loader2, Edit2, CheckCircle2, Banknote, CreditCard, SplitSquareHorizontal } from "lucide-react";
import InvoiceShareModal from "@/components/InvoiceShareModal";

/**
 * Edit modal for an existing transaction (admin/owner only).
 * Props:
 *   tx        — transaction to edit
 *   onClose()
 *   onSaved(updatedTx)
 */
export default function EditTransactionModal({ tx, onClose, onSaved }) {
  const { t, lang } = useLang();
  const [services, setServices] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [customerPhone, setCustomerPhone] = useState(tx.customer_phone);
  const [items, setItems] = useState(
    tx.items.map((it) => ({
      service_id: it.service_id,
      therapist_id: it.therapist_id,
      // Prefer explicit flags; fall back to legacy is_upsell (which was
      // always treated as bonus 10% by the old code path).
      bonus_5: !!it.bonus_5,
      bonus_10: !!it.bonus_10 || (!it.bonus_5 && !it.bonus_10 && !!it.is_upsell),
    })),
  );
  const [discountType, setDiscountType] = useState(tx.discount_type);
  const [discountValue, setDiscountValue] = useState(tx.discount_value);
  const [paymentMethod, setPaymentMethod] = useState(tx.payment_method || "cash");
  const [splitCash, setSplitCash] = useState(tx.payment_method === "split" ? String(tx.cash_amount || "") : "");
  const [splitCard, setSplitCard] = useState(tx.payment_method === "split" ? String(tx.card_amount || "") : "");
  const [busy, setBusy] = useState(false);
  const [savedTx, setSavedTx] = useState(null);  // when set → show InvoiceShareModal

  // Live-derived phone validation (same contract as KasirPage).
  const _trimmedPhone = (customerPhone || "").trim();
  const _phoneCheck = _trimmedPhone ? validateCustomerPhone(customerPhone) : null;
  const phoneValid = !!_phoneCheck?.ok;
  const phoneErrKey = _phoneCheck && !_phoneCheck.ok
    ? (_phoneCheck.reason === "spa" ? "kasir.phoneSpaForbidden" : "kasir.phoneInvalid")
    : null;

  // Backdate editor — pre-fill with the tx's current time in Riyadh local
  // format (yyyy-mm-ddThh:mm) so <input type="datetime-local"> accepts it.
  const initialCreatedAt = useMemo(() => {
    try {
      const d = new Date(tx.created_at);
      // Convert to Asia/Riyadh wall-clock
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
      }).formatToParts(d);
      const g = (k) => parts.find((p) => p.type === k).value;
      return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
    } catch { return ""; }
  }, [tx.created_at]);
  const [createdAt, setCreatedAt] = useState(initialCreatedAt);

  // Original item count (snapshot at mount) — used to detect "ada tambahan layanan"
  const originalCount = useMemo(() => tx.items.length, [tx.items.length]);
  const hasNewItems = items.length > originalCount;

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/therapists").then((r) => setTherapists(r.data));
  }, []);

  const enriched = items.map((it) => ({
    ...it,
    svc: services.find((s) => s.id === it.service_id),
    th: therapists.find((u) => u.id === it.therapist_id),
  }));

  const subtotal = enriched.reduce((s, x) => s + (x.svc?.price || 0), 0);
  const discountAmt = useMemo(() => {
    if (discountType === "percent") return Math.min((subtotal * (discountValue || 0)) / 100, subtotal);
    if (discountType === "amount") return Math.min(discountValue || 0, subtotal);
    return 0;
  }, [discountType, discountValue, subtotal]);
  const total = Math.max(subtotal - discountAmt, 0);

  const svcLabel = (s) => `${lang === "ar" ? s.name_ar : (lang === "en" ? (s.name_en || s.name_id) : s.name_id)} — ${formatSAR(s.price)}`;

  const updateItem = useCallback((i, patch) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, ...patch } : it))), []);
  const addItem = useCallback(() => setItems((p) => [...p, { service_id: "", therapist_id: "", bonus_5: false, bonus_10: false }]), []);
  const removeItem = useCallback((i) => setItems((p) => p.filter((_, idx) => idx !== i)), []);

  async function save() {
    if (!customerPhone.trim()) return toast.error(t("kasir.customerPhone"));
    const phoneCheck = validateCustomerPhone(customerPhone);
    if (!phoneCheck.ok) {
      return toast.error(t(phoneCheck.reason === "spa" ? "kasir.phoneSpaForbidden" : "kasir.phoneInvalid"));
    }
    if (items.length === 0) return toast.error(t("kasir.addItem"));
    if (items.some((it) => !it.service_id || !it.therapist_id))
      return toast.error(`${t("kasir.selectService")} & ${t("kasir.selectTherapist")}`);

    const payloadItems = enriched.map((it) => ({
      service_id: it.svc.id,
      service_name_id: it.svc.name_id,
      service_name_ar: it.svc.name_ar,
      price: it.svc.price,
      therapist_id: it.th.id,
      therapist_name: it.th.name,
      is_upsell: !!(it.bonus_5 || it.bonus_10),
      bonus_5: !!it.bonus_5,
      bonus_10: !!it.bonus_10,
    }));
    const upsell_item_ids = enriched.filter((it) => it.bonus_5 || it.bonus_10).map((it) => it.svc.id);

    // Client-side validate split payment (mirror backend)
    let cashAmt = 0, cardAmt = 0;
    if (paymentMethod === "split") {
      cashAmt = Number(splitCash) || 0;
      cardAmt = Number(splitCard) || 0;
      // Total will be recomputed by backend, but we can pre-check that both sides are entered
      if (cashAmt <= 0 || cardAmt <= 0) {
        return toast.error(t("kasir.splitBothRequired"));
      }
    }

    setBusy(true);
    try {
      // Only include created_at in payload if the user actually changed it
      // (avoids gratuitous timestamp writes on plain edits).
      const createdAtChanged = createdAt && createdAt !== initialCreatedAt;
      const { data } = await api.put(`/transactions/${tx.id}`, {
        customer_phone: phoneCheck.phone,
        items: payloadItems,
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        upsell_item_ids,
        payment_method: paymentMethod,
        cash_amount: cashAmt,
        card_amount: cardAmt,
        ...(createdAtChanged ? { created_at: `${createdAt}:00+03:00` } : {}),
      });
      toast.success(t("tx.editSuccess"));
      if (hasNewItems) {
        // Tampilkan struk transaksi setelah penambahan layanan
        setSavedTx(data);
      } else {
        onSaved?.(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  }

  // If receipt is being shown, render it as overlay instead of edit form
  if (savedTx) {
    const m = savedTx.payment_method || "cash";
    const payLabel = m === "split"
      ? `${t("kasir.paySplit")} · ${t("kasir.payCash")} ${formatSAR(savedTx.cash_amount || 0)} + ${t("kasir.payCard")} ${formatSAR(savedTx.card_amount || 0)}`
      : (m === "card" ? t("kasir.payCard") : t("kasir.payCash"));
    return (
      <InvoiceShareModal
        tx={savedTx}
        customerName={savedTx.customer_name || ""}
        paymentMethod={payLabel}
        onClose={() => {
          setSavedTx(null);
          onSaved?.(savedTx);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-3 md:p-4" onClick={onClose}>
      <div className="card-luxury w-full max-w-2xl max-h-[92vh] overflow-y-auto scrollbar-thin" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-charcoal-800/95 backdrop-blur-xl border-b border-gold/15 p-5 flex justify-between items-center z-10">
          <h2 className="text-xl font-heading flex items-center gap-2">
            <Edit2 className="h-5 w-5 text-gold" /> {t("tx.edit")}
          </h2>
          <button onClick={onClose} data-testid="edit-modal-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("kasir.customerPhone")}</label>
              <input
                value={customerPhone}
                onChange={(e) => {
                  // Enforce "+966 " prefix stickiness + 9-digit cap.
                  const PREFIX = "+966 ";
                  let v = e.target.value;
                  if (!v.startsWith(PREFIX)) {
                    const digits = v.replace(/\D/g, "").replace(/^966/, "");
                    v = PREFIX + digits;
                  }
                  const after = v.slice(PREFIX.length).replace(/\D/g, "").slice(0, 9);
                  setCustomerPhone(PREFIX + after);
                }}
                inputMode="numeric"
                maxLength={14}
                placeholder="+966 5XXXXXXXX"
                data-testid="edit-phone"
                aria-invalid={phoneErrKey ? "true" : "false"}
                className={`w-full bg-charcoal-900 border rounded-lg px-3 py-2 transition-colors ${
                  phoneErrKey
                    ? "border-red-500/70 focus:border-red-500"
                    : phoneValid
                      ? "border-leaf/60 focus:border-leaf"
                      : "border-charcoal-600 focus:border-gold"
                }`}
              />
              {phoneErrKey && (
                <p data-testid="edit-phone-error" className="mt-1 text-xs text-red-400">
                  {t(phoneErrKey)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-gold mb-2">
                Waktu Transaksi
                {createdAt && createdAt !== initialCreatedAt && (
                  <span className="ms-2 text-[10px] normal-case tracking-normal text-leaf">(diubah)</span>
                )}
              </label>
              <input
                type="datetime-local"
                data-testid="edit-created-at"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                className="w-full bg-charcoal-900 border border-gold/40 focus:border-gold rounded-lg px-3 py-2 text-sm"
              />
              <div className="text-[10px] text-foreground/40 mt-1">Waktu Riyadh. Ubah untuk memperbaiki tanggal jika perlu.</div>
            </div>
          </div>

          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid md:grid-cols-12 gap-2 items-start p-3 bg-charcoal-900/50 rounded-lg border border-gold/10">
                <div className="md:col-span-5">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("kasir.service")}</label>
                  <select
                    data-testid={`edit-service-${i}`}
                    value={it.service_id}
                    onChange={(e) => updateItem(i, { service_id: e.target.value })}
                    className="w-full bg-charcoal-800 border border-charcoal-600 rounded-md px-2 py-1.5 text-sm"
                  >
                    <option value="">— {t("kasir.selectService")} —</option>
                    {services.map((s) => <option key={s.id} value={s.id}>{svcLabel(s)}</option>)}
                  </select>
                </div>
                <div className="md:col-span-4">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("kasir.therapist")}</label>
                  <select
                    data-testid={`edit-therapist-${i}`}
                    value={it.therapist_id}
                    onChange={(e) => updateItem(i, { therapist_id: e.target.value })}
                    className="w-full bg-charcoal-800 border border-charcoal-600 rounded-md px-2 py-1.5 text-sm"
                  >
                    <option value="">— {t("kasir.selectTherapist")} —</option>
                    {therapists.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2 pt-5 flex flex-col gap-1">
                  <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`edit-bonus5-${i}`}
                      checked={!!it.bonus_5}
                      onChange={(e) => updateItem(i, { bonus_5: e.target.checked })}
                      className="accent-leaf h-3.5 w-3.5"
                    />
                    <span className="text-leaf">Bonus (+5%)</span>
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`edit-bonus10-${i}`}
                      checked={!!it.bonus_10}
                      onChange={(e) => updateItem(i, { bonus_10: e.target.checked })}
                      className="accent-gold h-3.5 w-3.5"
                    />
                    <span className="text-gold">Bonus (+10%)</span>
                  </label>
                </div>
                <div className="md:col-span-1 pt-5 flex justify-end">
                  {items.length > 1 && (
                    <button onClick={() => removeItem(i)} className="p-1.5 rounded-full hover:bg-red-500/10 text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button onClick={addItem} className="btn-outline-gold w-full justify-center text-sm py-2">
              <Plus className="h-4 w-4" /> {t("kasir.addItem")}
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("kasir.discount")}</label>
              <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-2 py-2">
                <option value="none">{t("kasir.none")}</option>
                <option value="percent">{t("kasir.discountPercent")}</option>
                <option value="amount">{t("kasir.discountAmount")}</option>
              </select>
            </div>
            {discountType !== "none" && (
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">
                  {discountType === "percent" ? "%" : "SAR"}
                </label>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={discountValue === 0 ? "" : discountValue}
                  onChange={(e) => setDiscountValue(e.target.value === "" ? 0 : Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-2 py-2"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("kasir.paymentMethod")}</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setPaymentMethod("cash")}
                data-testid="edit-pay-cash"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 transition active:scale-95 text-sm ${
                  paymentMethod === "cash"
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-charcoal-600 bg-charcoal-900 text-foreground/60 hover:border-gold/40"
                }`}
              >
                <Banknote className="h-4 w-4" /> {t("kasir.payCash")}
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("card")}
                data-testid="edit-pay-card"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 transition active:scale-95 text-sm ${
                  paymentMethod === "card"
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-charcoal-600 bg-charcoal-900 text-foreground/60 hover:border-gold/40"
                }`}
              >
                <CreditCard className="h-4 w-4" /> {t("kasir.payCard")}
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("split")}
                data-testid="edit-pay-split"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 transition active:scale-95 text-sm ${
                  paymentMethod === "split"
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-charcoal-600 bg-charcoal-900 text-foreground/60 hover:border-gold/40"
                }`}
              >
                <SplitSquareHorizontal className="h-4 w-4" /> {t("kasir.paySplit")}
              </button>
            </div>

            {paymentMethod === "split" && (
              <div className="mt-3 grid grid-cols-2 gap-3" data-testid="edit-split-inputs">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-leaf mb-1">
                    <Banknote className="h-3 w-3 inline me-1" /> {t("kasir.payCash")} (SAR)
                  </label>
                  <input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={splitCash}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSplitCash(v);
                      if (v !== "" && total > 0) {
                        const rem = Math.max(total - (Number(v) || 0), 0);
                        setSplitCard(rem > 0 ? rem.toFixed(2) : "");
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    className="w-full bg-charcoal-900 border border-leaf/30 focus:border-leaf rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-gold mb-1">
                    <CreditCard className="h-3 w-3 inline me-1" /> {t("kasir.payCard")} (SAR)
                  </label>
                  <input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={splitCard}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSplitCard(v);
                      if (v !== "" && total > 0) {
                        const rem = Math.max(total - (Number(v) || 0), 0);
                        setSplitCash(rem > 0 ? rem.toFixed(2) : "");
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    className="w-full bg-charcoal-900 border border-gold/30 focus:border-gold rounded-md px-3 py-2"
                  />
                </div>
                {(() => {
                  const c = Number(splitCash) || 0;
                  const k = Number(splitCard) || 0;
                  const sum = c + k;
                  const ok = c > 0 && k > 0 && Math.abs(total - sum) < 0.01;
                  return (
                    <div className={`col-span-2 text-xs px-3 py-2 rounded-md ${ok ? "bg-leaf/10 text-leaf" : "bg-red-500/10 text-red-300"}`}>
                      {ok ? `✓ ${formatSAR(c)} + ${formatSAR(k)} = ${formatSAR(sum)}` : `Total input ${formatSAR(sum)} · Grand total ${formatSAR(total)}`}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="bg-charcoal-900/70 border border-gold/20 rounded-xl p-4 space-y-1">
            <div className="flex justify-between text-sm"><span>{t("kasir.subtotal")}</span><span>{formatSAR(subtotal)}</span></div>
            {discountAmt > 0 && <div className="flex justify-between text-sm text-leaf"><span>{t("kasir.discount")}</span><span>−{formatSAR(discountAmt)}</span></div>}
            <div className="flex justify-between pt-2 border-t border-gold/10">
              <span className="font-heading">{t("kasir.total")}</span>
              <span className="font-heading text-2xl text-gold" data-testid="edit-total">{formatSAR(total)}</span>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-charcoal-800/95 backdrop-blur-xl border-t border-gold/15 p-4 flex gap-3">
          <button onClick={onClose} className="btn-outline-gold flex-1 justify-center">{t("common.cancel")}</button>
          <button onClick={save} disabled={busy || !phoneValid} data-testid="edit-save-btn" className="btn-gold flex-1 justify-center disabled:opacity-60">
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : hasNewItems ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {hasNewItems ? t("kasir.submit") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
